import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';

const INDUSTRIES = ['E-commerce', 'Technology', 'Retail', 'Healthcare', 'Finance', 'Manufacturing', 'Education', 'Real Estate', 'Consulting', 'Media & Entertainment', 'Transportation', 'Hospitality', 'Telecommunications', 'Other'];
const SOURCES = ['Website', 'Contact Form', 'Get a Demo', 'Get a Call Back', 'LinkedIn', 'Referral', 'Cold Call', 'Email Campaign', 'Conference', 'Social Media', 'Self', 'Marketing', 'Internal Ref', 'Client Reference', 'Existing Client', 'Churned Client', 'Partner- India', 'Partner- Intl', 'OEM-VISPL', 'OEM-OXT', 'Other'];
const EMPLOYEE_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];
// Routing primitive — use this for stable, source-format-independent rules.
const LEAD_CHANNELS = ['Website', 'Outbound', 'Inbound', 'Referral', 'Marketing', 'Partner', 'Existing Client', 'Other'];

function AssignmentRulesPage() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    assignee_id: '',
    priority: 1,
    active: true,
    criteria: {
      industry: '',
      source: '',
      employee_size: '',
      lead_channel: '',
    }
  });

  const fetchRules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assignment-rules`);
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching rules:', error);
      showMessage('error', 'Failed to fetch assignment rules');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      const assignableUsers = response.data.filter(u => 
        u.is_active !== false && 
        ['sdr', 'sales_executive', 'team_leader', 'manager', 'regional_sales_head', 'country_sales_head'].includes(u.role)
      );
      setUsers(assignableUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRules(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRules, fetchUsers]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      assignee_id: '',
      priority: 1,
      active: true,
      criteria: {
        industry: '',
        source: '',
        employee_size: '',
        lead_channel: '',
      }
    });
    setEditingRule(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name || '',
      assignee_id: rule.assignee_id || '',
      priority: rule.priority || 1,
      active: rule.active !== false,
      criteria: {
        industry: rule.criteria?.industry || '',
        source: rule.criteria?.source || '',
        employee_size: rule.criteria?.employee_size || '',
        lead_channel: rule.criteria?.lead_channel || '',
      }
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.assignee_id) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }

    const hasCriteria = formData.criteria.industry || formData.criteria.source || formData.criteria.employee_size;
    if (!hasCriteria) {
      showMessage('error', 'Please select at least one criteria');
      return;
    }

    setActionLoading(true);
    try {
      const cleanCriteria = {};
      if (formData.criteria.industry) cleanCriteria.industry = formData.criteria.industry;
      if (formData.criteria.source) cleanCriteria.source = formData.criteria.source;
      if (formData.criteria.employee_size) cleanCriteria.employee_size = formData.criteria.employee_size;

      const payload = {
        name: formData.name,
        assignee_id: formData.assignee_id,
        priority: parseInt(formData.priority),
        active: formData.active,
        criteria: cleanCriteria,
      };

      if (editingRule) {
        await axios.put(`${API}/assignment-rules/${editingRule.id}`, payload);
        showMessage('success', 'Rule updated successfully');
      } else {
        await axios.post(`${API}/assignment-rules`, payload);
        showMessage('success', 'Rule created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchRules();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this rule? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/assignment-rules/${ruleId}`);
      showMessage('success', 'Rule deleted');
      fetchRules();
    } catch (error) {
      showMessage('error', 'Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await axios.put(`${API}/assignment-rules/${rule.id}`, {
        ...rule,
        active: !rule.active,
      });
      fetchRules();
    } catch (error) {
      showMessage('error', 'Failed to update rule status');
    }
  };

  const formatRole = (role) => {
    const roleLabels = {
      admin: 'Admin',
      company_sales_head: 'Company Head',
      country_sales_head: 'Country Head',
      regional_sales_head: 'Regional Head',
      manager: 'Manager',
      team_leader: 'Team Leader',
      sales_executive: 'Sales Exec',
      sdr: 'SDR',
    };
    return roleLabels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      manager: 'bg-blue-100 text-blue-700',
      team_leader: 'bg-purple-100 text-purple-700',
      sales_executive: 'bg-cyan-100 text-cyan-700',
      sdr: 'bg-green-100 text-green-700',
    };
    return colors[role] || 'bg-slate-100 text-slate-700 dark:text-slate-300';
  };

  const getCriteriaDisplay = (criteria) => {
    if (!criteria) return 'No criteria';
    
    const parts = [];
    if (criteria.lead_channel) parts.push(`Channel: ${criteria.lead_channel}`);
    if (criteria.industry) parts.push(`Industry: ${criteria.industry}`);
    if (criteria.source) parts.push(`Source: ${criteria.source}`);
    if (criteria.employee_size) parts.push(`Size: ${criteria.employee_size}`);
    
    return parts.length > 0 ? parts.join(' • ') : 'No criteria';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="animate-spin w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assignment-rules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Assignment Rules</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Automatically assign leads to team members based on criteria</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition flex items-center gap-2"
          data-testid="create-rule-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Rule
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`} data-testid="message-alert">
          {message.type === 'success' ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{rules.length}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Rules</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{rules.filter(r => r.active).length}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Active Rules</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{rules.filter(r => !r.active).length}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Inactive Rules</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">How Assignment Rules Work</h3>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <li>• Rules are evaluated in <strong>priority order</strong> (highest number first) when auto-assigning leads</li>
              <li>• The first matching rule determines the assignee</li>
              <li>• Criteria can include: Industry, Lead Source, and Company Size</li>
              <li>• If no rules match, the lead remains assigned to the creator</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Rules List */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">All Rules ({rules.length})</h3>
        </div>
        
        {rules.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-12 h-12 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="font-medium text-slate-700 dark:text-slate-300 mb-1">No assignment rules yet</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Create your first rule to automate lead distribution</p>
            <button
              onClick={openCreateModal}
              className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 text-sm"
            >
              Create your first rule
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {[...rules].sort((a, b) => (b.priority || 0) - (a.priority || 0)).map((rule) => (
              <div key={rule.id} className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition ${!rule.active ? 'opacity-60' : ''}`} data-testid={`rule-row-${rule.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    {/* Priority Badge */}
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-700 font-bold text-sm">{rule.priority}</span>
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-slate-900 dark:text-white">{rule.name}</p>
                        <button
                          onClick={() => handleToggleActive(rule)}
                          className={`px-2 py-0.5 text-xs rounded-full font-medium transition ${
                            rule.active 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-slate-100 text-slate-500 dark:text-slate-400 hover:bg-slate-200'
                          }`}
                        >
                          {rule.active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                      
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{getCriteriaDisplay(rule.criteria)}</p>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">Assigns to:</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                            {rule.assignee_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{rule.assignee_name || 'Unknown'}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getRoleBadgeColor(rule.assignee_role)}`}>
                            {formatRole(rule.assignee_role)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEditModal(rule)}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Edit Rule"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(rule.id)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Delete Rule"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingRule ? 'Edit Rule' : 'Create New Rule'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Rule Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                  placeholder="e.g., E-commerce leads to John"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Assign To *</label>
                <select
                  value={formData.assignee_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignee_id: e.target.value }))}
                  required
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                >
                  <option value="">Select team member</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({formatRole(u.role)})</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Priority (Higher = First)</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                  min={1}
                  max={100}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                />
              </div>
              
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-3">Criteria (select at least one)</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                      Channel
                      <span className="ml-2 text-[10px] font-normal text-emerald-600 dark:text-emerald-400">⭐ recommended for routing</span>
                    </label>
                    <select
                      value={formData.criteria.lead_channel || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, criteria: { ...prev.criteria, lead_channel: e.target.value }}))}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      data-testid="rule-channel-select"
                    >
                      <option value="">Any Channel</option>
                      {LEAD_CHANNELS.map(ch => (
                        <option key={ch} value={ch}>{ch}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                      Auto-derived from <code>source</code> on create. Use this for stable rules — e.g. one "Channel = Website" rule covers Contact Form / Get a Demo / Get a Call Back.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Industry</label>
                    <select
                      value={formData.criteria.industry}
                      onChange={(e) => setFormData(prev => ({ ...prev, criteria: { ...prev.criteria, industry: e.target.value }}))}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    >
                      <option value="">Any Industry</option>
                      {INDUSTRIES.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                      Lead Source
                      <span className="ml-2 text-[10px] font-normal text-slate-400">granular — use Channel above for routing</span>
                    </label>
                    <select
                      value={formData.criteria.source}
                      onChange={(e) => setFormData(prev => ({ ...prev, criteria: { ...prev.criteria, source: e.target.value }}))}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    >
                      <option value="">Any Source</option>
                      {SOURCES.map(src => (
                        <option key={src} value={src}>{src}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Company Size</label>
                    <select
                      value={formData.criteria.employee_size}
                      onChange={(e) => setFormData(prev => ({ ...prev, criteria: { ...prev.criteria, employee_size: e.target.value }}))}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    >
                      <option value="">Any Size</option>
                      {EMPLOYEE_SIZES.map(size => (
                        <option key={size} value={size}>{size} employees</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="active" className="text-sm text-slate-600 dark:text-slate-400">Rule is active</label>
              </div>
              
              <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 dark:text-slate-100">
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={actionLoading}
                  className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignmentRulesPage;
